import type { JSX } from 'react'

import { trailerPlayer } from '@setplex/pureya'
import { useUnit } from 'effector-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Intent as AppIntent,
  model,
  PlayerIntentMessage,
} from '~/entities/player'
import { entitiesPlayerTrailerModel } from '~/entities/player/trailer'
import { mq } from '~/shared/mq'
import { usePlayer } from '~/widgets/Player'
import { PlayerWrap } from '~/widgets/Player/PlayerWrap'
import { model as infoPopupModel } from '~/widgets/Popups/InfoPopup'
import { useVisibilityTracking } from './lib'

export const PlayerTrailer = (): JSX.Element | null => {
  const player = usePlayer()
  const [isReady, setIsReady] = useState(false)
  const [isPlayerVisible, setIsPlayerVisible] = useState(true)

  const contentId = useUnit(entitiesPlayerTrailerModel.$trailerContentId)
  const contentType = useUnit(entitiesPlayerTrailerModel.$trailerContentType)
  const contentName = useUnit(entitiesPlayerTrailerModel.$trailerContentName)
  const contentFree = useUnit(entitiesPlayerTrailerModel.$trailerContentFree)
  const trailerUrl = useUnit(entitiesPlayerTrailerModel.$trailerUrl)
  const isTrailerPlayerVisible = useUnit(
    entitiesPlayerTrailerModel.$isTrailerPlayerVisible
  )
  const setTrailerDuration = useUnit(
    entitiesPlayerTrailerModel.setTrailerDuration
  )
  const onTrailerEnded = useUnit(entitiesPlayerTrailerModel.trailerEnded)
  const onTrailerError = useUnit(entitiesPlayerTrailerModel.trailerError)
  const infoPopup = useUnit(infoPopupModel.$infoPopup)

  const isSmartlibEnabled = useUnit(model.$isSmartlibEnabled)
  const prevIsPlayerVisibleRef = useRef<boolean | null>(null)

  const handleLoadedMetadata = () => {
    const duration = player.instance?.duration?.()
    if (typeof duration === 'number' && !isNaN(duration) && duration > 0) {
      setTrailerDuration(duration)
    }
  }

  const handleError = () => {
    onTrailerError()
  }

  player.events?.on?.('loadedmetadata:trailers', handleLoadedMetadata)
  player.events?.on?.('error:trailers', handleError)
  player.events?.on?.('dispose:trailers', () => setIsReady(false))

  // Pause/play based on viewport visibility (after trailer is visible)
  useEffect(() => {
    const instance = player.instance
    if (!instance || !isTrailerPlayerVisible) return

    const prevIsPlayerVisible = prevIsPlayerVisibleRef.current
    const didVisibilityFlip =
      prevIsPlayerVisible !== null && prevIsPlayerVisible !== isPlayerVisible
    prevIsPlayerVisibleRef.current = isPlayerVisible

    if (isPlayerVisible) {
      if (didVisibilityFlip) {
        mq.emit(
          new PlayerIntentMessage(instance, AppIntent.TrailerVisibleByScroll)
        )
      }
      instance.play()
    } else {
      if (didVisibilityFlip) {
        mq.emit(
          new PlayerIntentMessage(instance, AppIntent.TrailerHiddenByScroll)
        )
      }
      instance.pause()
    }
  }, [isPlayerVisible, player.instance, isTrailerPlayerVisible, isReady])

  // Pause when info popup opens, resume when it closes
  useEffect(() => {
    const instance = player.instance
    if (!instance || !isTrailerPlayerVisible) return

    if (infoPopup.condition) {
      instance.pause()
    } else {
      // Info popup is closed - resume playback if player should be visible
      if (isPlayerVisible) {
        instance.play()
      }
    }
  }, [
    infoPopup.condition,
    player.instance,
    isTrailerPlayerVisible,
    isPlayerVisible,
  ])

  // Memoize callback to prevent unnecessary re-subscriptions
  const handleVisibilityChange = useCallback(
    (isVisible: boolean) => setIsPlayerVisible(isVisible),
    []
  )

  // IntersectionObserver for visibility tracking
  useVisibilityTracking({
    container: player.container as Element | null,
    enabled: isTrailerPlayerVisible,
    onVisibilityChange: handleVisibilityChange,
  })

  // Defer source until player ready — ensures XHR hook is attached before manifest request
  const mediaUrlData = useMemo(
    () => (isReady && trailerUrl ? trailerUrl : null),
    [isReady, trailerUrl]
  )

  return (
    <PlayerWrap
      player={player}
      mediaUrlData={mediaUrlData}
      using={trailerPlayer}
      title={contentName ?? undefined}
      contentKey={contentId ?? undefined}
      contentId={String(contentId || '')}
      contentType={contentType}
      contentProgram=''
      blockedByAcl={false}
      safeToShow={true}
      isTrailer={true}
      muted
      autoplay={false}
      npawData={null}
      isSmartlibEnabled={isSmartlibEnabled}
      onPlaybackEnded={onTrailerEnded}
      visible={isTrailerPlayerVisible}
      onReady={() => setIsReady(true)}
      autoplayTrailer={true}
      isContentPaid={contentFree === false}
    />
  )
}
